import { ChevronDownIcon } from "@radix-ui/react-icons";
import {
  Children,
  isValidElement,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { Components } from "react-markdown";
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from "~/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { extractReferenceContext } from "~/utils/markdownTerms";
import {
  CITATION_ANCHOR_SELECTOR,
  CITATION_GROUP_CLASS,
  GROUPABLE_CITATION_CLASSES,
} from "./rehypeGroupSourceCitations";
import { useReportContainer } from "./reportContainerContext";

const TOOLTIP_LABEL_THRESHOLD = 20;

function getReactNodeText(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(getReactNodeText).join("");
  if (isValidElement<{ children?: ReactNode }>(node)) {
    return getReactNodeText(node.props.children);
  }
  return "";
}

type CitationProps = {
  children?: ReactNode;
  title?: unknown;
  "data-first-page-id"?: unknown;
  "data-page-id"?: unknown;
};

function getCitationLabel(props: CitationProps): string {
  return getReactNodeText(props.children);
}

function getCitationTitle(props: CitationProps): string {
  if (typeof props.title === "string" && props.title) {
    return props.title;
  }
  return getCitationLabel(props);
}

function isDocumentScopedCitation(props: CitationProps): boolean {
  return Boolean(props["data-first-page-id"]) && !props["data-page-id"];
}

function getCitationDisplayLabel(
  props: CitationProps,
  documentScopeLabel: string
): string {
  if (isDocumentScopedCitation(props)) {
    return documentScopeLabel;
  }
  return getCitationLabel(props);
}

/**
 * Number of citation rows that stay visible before the dropdown scrolls.
 */
const MAX_VISIBLE_CITATIONS = 10;

/**
 * Rendered height (px) of a single citation row (px-2 py-1.5 padding + badge
 * line height). Used to cap the dropdown so that at most
 * MAX_VISIBLE_CITATIONS rows fit before a vertical scrollbar appears.
 */
const CITATION_ROW_HEIGHT_PX = 32;

interface ReportSourceCitationGroupProps {
  children?: ReactNode;
}

/**
 * Collapses a run of consecutive inline citation badges into a single
 * clickable trigger (count + chevron) that opens a dropdown/popover listing
 * every badge.
 *
 * The badges themselves are the original `source-map-link` / `bates-link`
 * anchors, so clicking one inside the popover still opens the source modal via
 * the existing report-level click delegation. The popover content is portaled
 * into the report container (see {@link useReportContainer}) so the anchors
 * stay inside the delegation root while keeping the DOM valid (no block
 * element nested in the `<p>`). It closes on click outside / Escape (Radix
 * Popover) as well as when a badge is clicked.
 *
 * Because the badges are portaled out of their paragraph, their surrounding
 * prose is no longer reachable from the anchor. The trigger button stays at the
 * run's original in-paragraph position, so on open we derive the reference
 * context from it and expose it on the popover content via
 * `data-reference-context`; the click-delegation handler then reads it back
 * through {@link extractReferenceContext}, preserving the source heatmap.
 */
export const ReportSourceCitationGroup = ({
  children,
}: ReportSourceCitationGroupProps) => {
  const { formatMessage } = useIntl();
  const reportContainer = useReportContainer();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [referenceContext, setReferenceContext] = useState("");

  const citations = Children.toArray(children).filter(
    (child): child is ReactElement<CitationProps> =>
      isValidElement<CitationProps>(child)
  );

  if (citations.length === 0) {
    return null;
  }

  const handleOpenChange = (next: boolean) => {
    if (next && triggerRef.current) {
      setReferenceContext(extractReferenceContext(triggerRef.current));
    }
    setOpen(next);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          aria-label={formatMessage(
            { id: "report.citations.show" },
            { count: citations.length }
          )}
          className="group ml-[2px] inline-flex h-[1.4em] cursor-pointer items-center gap-0.5 align-middle text-[0.7em] font-normal leading-none transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring relative top-[-0.05em]"
        >
          <span className="inline-flex h-[1.4em] items-center rounded-full bg-muted-foreground/20 px-[0.45em] leading-none text-muted-foreground hover:bg-muted-foreground/30">
            <ChevronDownIcon className="size-[1em] shrink-0" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverPortal container={reportContainer ?? undefined}>
        <PopoverContent
          align="start"
          className="w-auto min-w-32 max-w-xs p-1"
          data-reference-context={referenceContext || undefined}
          onClick={() => {
            setOpen(false);
          }}
        >
          <div
            className="overflow-y-auto"
            style={{
              maxHeight: MAX_VISIBLE_CITATIONS * CITATION_ROW_HEIGHT_PX,
            }}
          >
            {citations.map((citation, index) => {
              const citationProps = citation.props;
              const rawTitle = getCitationTitle(citationProps);
              const title = isDocumentScopedCitation(citationProps)
                ? getCitationDisplayLabel(
                    citationProps,
                    formatMessage(
                      { id: "report.citations.documentScope" },
                      { title: rawTitle }
                    )
                  )
                : rawTitle;
              const label = getCitationLabel(citationProps);
              return (
                <div
                  key={index}
                  role="button"
                  tabIndex={0}
                  title={title}
                  className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors hover:bg-accent"
                  onClick={(e) => {
                    const anchor = (
                      e.currentTarget as HTMLElement
                    ).querySelector(
                      CITATION_ANCHOR_SELECTOR
                    ) as HTMLElement | null;
                    if (
                      anchor &&
                      e.target !== anchor &&
                      !anchor.contains(e.target as Node)
                    ) {
                      anchor.click();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      const anchor = (
                        e.currentTarget as HTMLElement
                      ).querySelector(
                        CITATION_ANCHOR_SELECTOR
                      ) as HTMLElement | null;
                      anchor?.click();
                    }
                  }}
                >
                  <span className="sr-only">{citation}</span>
                  <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-muted-foreground/20 px-1.5 text-xs leading-none text-muted-foreground">
                    {label}
                  </span>
                  <span className="truncate">{title}</span>
                </div>
              );
            })}
          </div>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};

/**
 * react-markdown component overrides that render collapsed citation groups and
 * add a tooltip to long inline citation badges.
 *
 * The grouping rehype plugin wraps consecutive citation anchors in a
 * `<span class="report-citation-group">`; this override detects that marker and
 * renders the dropdown, while every other span renders normally.
 *
 * Single (non-grouped) citation anchors whose label exceeds
 * TOOLTIP_LABEL_THRESHOLD characters get a Radix Tooltip so the full text is
 * readable without expanding the badge into surrounding prose.
 */
export const reportCitationComponents: Components = {
  a: ({ node: _node, className, children, ...props }) => {
    const classes = typeof className === "string" ? className.split(/\s+/) : [];
    const isCitationLink = GROUPABLE_CITATION_CLASSES.some((cls) =>
      classes.includes(cls)
    );
    const citationProps: CitationProps = { ...props, children };
    const documentScoped =
      isCitationLink && isDocumentScopedCitation(citationProps);
    const documentTitle = isCitationLink ? getCitationTitle(citationProps) : "";
    const displayLabel = documentScoped
      ? documentTitle
      : getReactNodeText(children);

    if (isCitationLink) {
      const label = getReactNodeText(children);
      if (label.length > TOOLTIP_LABEL_THRESHOLD) {
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                className={className}
                data-long-label="true"
                aria-label={displayLabel}
                {...props}
              >
                {documentScoped ? (
                  <FormattedMessage
                    id="report.citations.documentScope"
                    values={{ title: displayLabel }}
                  />
                ) : (
                  children
                )}
              </a>
            </TooltipTrigger>
            <TooltipContent>{label}</TooltipContent>
          </Tooltip>
        );
      }
    }

    return (
      <a className={className} {...props}>
        {documentScoped ? (
          <FormattedMessage
            id="report.citations.documentScope"
            values={{ title: displayLabel }}
          />
        ) : (
          children
        )}
      </a>
    );
  },
  span: ({ node: _node, className, children, ...props }) => {
    const classes = typeof className === "string" ? className.split(/\s+/) : [];
    if (classes.includes(CITATION_GROUP_CLASS)) {
      return <ReportSourceCitationGroup>{children}</ReportSourceCitationGroup>;
    }
    return (
      <span className={className} {...props}>
        {children}
      </span>
    );
  },
};
